import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { notificationService } from '../services/notification.service';
import { signalRService } from '../services/signalr.service';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  isServiceWorkerReady: boolean;
  isSubscribed: boolean;
  isSignalRConnected: boolean;
  notificationPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  subscribeToNotifications: () => Promise<boolean>;
  unsubscribeFromNotifications: () => Promise<boolean>;
  connectSignalR: () => Promise<boolean>;
  disconnectSignalR: () => Promise<void>;
  onNotification: (callback: (data: any) => void) => void;
  offNotification: (callback: (data: any) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { state } = useAuth();
  const token = state.accessToken;
  const isAuthenticated = state.isAuthenticated;
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    notificationService.getPermission()
  );

  useEffect(() => {
    const initializeServiceWorker = async () => {
      const initialized = await notificationService.initialize();
      setIsServiceWorkerReady(initialized);

      if (initialized) {
        const subscription = await notificationService.getSubscription();
        setIsSubscribed(subscription !== null);
      }
    };

    initializeServiceWorker();
  }, []);

  // SignalR es independiente del Service Worker: conectar solo con autenticacion
  useEffect(() => {
    if (isAuthenticated && token && !isSignalRConnected) {
      console.log('[NotificationContext] Conectando SignalR con token...');
      connectSignalR();
    } else if (!isAuthenticated && isSignalRConnected) {
      console.log('[NotificationContext] Desconectando SignalR al cerrar sesión...');
      disconnectSignalR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, isSignalRConnected]);

  // Verificar y sincronizar suscripción existente al iniciar sesión
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (isAuthenticated && token && isServiceWorkerReady) {
        // Verificar si ya existe una suscripción en el navegador
        const subscription = await notificationService.getSubscription();

        if (subscription && !isSubscribed) {
          // Existe una suscripción pero el estado no está actualizado
          console.log('[NotificationContext] Suscripción existente detectada, sincronizando con backend...');

          // Actualizar estado local
          setIsSubscribed(true);

          // Sincronizar con el backend (actualizar idUsuario asociado)
          try {
            await notificationService.subscribeToPushNotifications(token);
            console.log('[NotificationContext] Suscripción sincronizada con backend');
          } catch (error) {
            console.error('[NotificationContext] Error sincronizando suscripción:', error);
          }
        } else if (subscription) {
          // La suscripción existe y el estado está correcto
          setIsSubscribed(true);
        } else {
          // No hay suscripción → auto-suscribir al login
          console.log('[NotificationContext] Sin suscripción push, solicitando permiso...');
          try {
            const permission = await notificationService.requestPermission();
            setNotificationPermission(permission);

            if (permission === 'granted' && token) {
              console.log('[NotificationContext] Permiso otorgado, suscribiendo automáticamente...');
              const sub = await notificationService.subscribeToPushNotifications(token);
              setIsSubscribed(sub !== null);
              if (sub) {
                console.log('[NotificationContext] Auto-suscripción push completada');
              }
            } else {
              setIsSubscribed(false);
            }
          } catch (error) {
            console.error('[NotificationContext] Error en auto-suscripción push:', error);
            setIsSubscribed(false);
          }
        }
      }
    };

    checkExistingSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, isServiceWorkerReady]);

  // Listeners estables para el estado de conexión SignalR (evita fuga de listeners)
  const handleConnectionClosed = useCallback(() => setIsSignalRConnected(false), []);
  const handleReconnected = useCallback(() => setIsSignalRConnected(true), []);

  useEffect(() => {
    signalRService.on('connectionClosed', handleConnectionClosed);
    signalRService.on('reconnected', handleReconnected);
    return () => {
      signalRService.off('connectionClosed', handleConnectionClosed);
      signalRService.off('reconnected', handleReconnected);
    };
  }, [handleConnectionClosed, handleReconnected]);

  const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    const permission = await notificationService.requestPermission();
    setNotificationPermission(permission);

    // Si se otorgan permisos, suscribir automáticamente
    if (permission === 'granted' && token && !isSubscribed) {
      console.log('[NotificationContext] Permisos otorgados, suscribiendo automáticamente...');
      await subscribeToNotifications();
    }

    return permission;
  };

  const subscribeToNotifications = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible');
      return false;
    }

    const subscription = await notificationService.subscribeToPushNotifications(token);
    const success = subscription !== null;
    setIsSubscribed(success);

    if (success) {
      console.log('Suscripción a notificaciones exitosa');
    }

    return success;
  };

  const unsubscribeFromNotifications = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible');
      return false;
    }

    const success = await notificationService.unsubscribeFromPushNotifications(token);
    if (success) {
      setIsSubscribed(false);
      console.log('Desuscripción de notificaciones exitosa');
    }

    return success;
  };

  const connectSignalR = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible para SignalR');
      return false;
    }

    const connected = await signalRService.connect(token);
    setIsSignalRConnected(connected);

    if (connected) {
      console.log('SignalR conectado exitosamente');
    }

    return connected;
  };

  const disconnectSignalR = async (): Promise<void> => {
    await signalRService.disconnect();
    setIsSignalRConnected(false);
  };

  const onNotification = (callback: (data: any) => void) => {
    signalRService.on('notification', callback);
  };

  const offNotification = (callback: (data: any) => void) => {
    signalRService.off('notification', callback);
  };

  const value: NotificationContextType = {
    isServiceWorkerReady,
    isSubscribed,
    isSignalRConnected,
    notificationPermission,
    requestNotificationPermission,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    connectSignalR,
    disconnectSignalR,
    onNotification,
    offNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
